import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "motion/react";
import { 
  X, 
  ChevronRight, 
  HelpCircle, 
  Sparkles, 
  GraduationCap, 
  Building2, 
  Cpu, 
  Compass, 
  Activity, 
  Settings, 
  CheckSquare, 
  Square,
  ArrowRight,
  RefreshCcw,
  Zap
} from "lucide-react";

interface NeedsQuizModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Industry = "education" | "property" | "technology" | "tourism" | "sports" | "engineering";

interface QuizState {
  step: 1 | 2 | 3;
  industry: Industry | null;
  challenges: string[];
}

const INDUSTRIES = [
  { id: "education" as Industry, name: "Education", icon: GraduationCap, desc: "LMS, student portals & online exams" },
  { id: "property" as Industry, name: "Property", icon: Building2, desc: "Leasing, listings & bookings" },
  { id: "technology" as Industry, name: "Technology", icon: Cpu, desc: "Cloud VPS, APIs & custom databases" },
  { id: "tourism" as Industry, name: "Tourism", icon: Compass, desc: "Hospitality & agency bookings" },
  { id: "sports" as Industry, name: "Sports & Gyms", icon: Activity, desc: "Member registries & schedules" },
  { id: "engineering" as Industry, name: "Engineering", icon: Settings, desc: "Supply chain & project monitoring" },
];

const CHALLENGES = [
  { id: "auth", text: "Secure user registration & login portal" },
  { id: "hosting", text: "Domain registration, server hosting & email" },
  { id: "analytics", text: "Visual dashboards for operational metrics" },
  { id: "db", text: "High-performance PostgreSQL database clusters" },
];

export const NeedsQuizModal: React.FC<NeedsQuizModalProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuth();
  const [quiz, setQuiz] = useState<QuizState>({
    step: 1,
    industry: null,
    challenges: []
  });

  if (!isOpen) return null;

  const handleSelectIndustry = (ind: Industry) => {
    setQuiz(prev => ({
      ...prev,
      industry: ind,
      step: 2
    }));
  };

  const handleToggleChallenge = (challengeId: string) => {
    setQuiz(prev => {
      const exists = prev.challenges.includes(challengeId);
      const updated = exists 
        ? prev.challenges.filter(c => c !== challengeId) 
        : [...prev.challenges, challengeId];
      return {
        ...prev,
        challenges: updated
      };
    });
  };

  const handleRestart = () => {
    setQuiz({
      step: 1,
      industry: null,
      challenges: []
    });
  };

  const handleLaunch = () => {
    if (quiz.industry) {
      localStorage.setItem("wesi_quiz_industry", quiz.industry);
    }
    if (quiz.challenges.length > 0) {
      localStorage.setItem("wesi_quiz_challenges", quiz.challenges.join(","));
    }

    onClose();
    if (isAuthenticated) {
      window.location.hash = "#dashboard";
    } else {
      // Dispatch custom event to open AuthModal in signup tab
      const event = new CustomEvent("open-auth-modal", { detail: { tab: "signup" } });
      window.dispatchEvent(event);
    }
  };

  // Logic to determine customized recommendation
  const getRecommendation = () => {
    const { industry, challenges } = quiz;
    if (!industry) return null;

    let title = "Wesi Enterprise Bundle";
    let desc = "A fully custom tailored cloud workspace engineered for your business needs.";
    let features: string[] = [];

    const hasAuth = challenges.includes("auth");
    const hasHosting = challenges.includes("hosting");
    const hasDb = challenges.includes("db");

    switch (industry) {
      case "education":
        title = "Wesi LMS + Academy Suite";
        desc = "Your dedicated academic portal with integrated lecture delivery and progress tracking.";
        features = [
          "🏫 Virtual classroom whiteboard integration",
          "🔒 Encrypted online exam security logs",
          "📊 Student analytics & progress tracking"
        ];
        break;
      case "property":
        title = "Wesi Property Broker Suite";
        desc = "A high-performance portal mapping tenant management, leasing workflows, and bookings.";
        features = [
          "🏢 Interactive real-time tenancy boards",
          "✉️ Direct mailbox notification servers",
          "📊 Booking frequency analytics"
        ];
        break;
      case "technology":
        title = "Wesi Cloud VPS Cluster";
        desc = "Secure hosting, domain management, and high-availability database nodes for tech firms.";
        features = [
          "🚀 Automated PostgreSQL DB replication",
          "🌐 Instant domain routing & email configurations",
          "🔒 256-bit AES client authentication portals"
        ];
        break;
      case "tourism":
        title = "Wesi Travel Booking Hub";
        desc = "Seamless CRM, tour packaging portals, and high-performance server clusters.";
        features = [
          "✈️ Dynamic schedule builder dashboards",
          "🔒 Direct secure guest logins",
          "🌐 Customized regional domain hosting"
        ];
        break;
      default:
        // sports / engineering / generic
        if (hasHosting || hasDb) {
          title = "Wesi Cloud Infrastructure Suite";
          desc = "A professional server-hosting, database cluster, and email deployment system.";
          features = [
            "🚀 High NVMe SSD virtual machines",
            "✉️ Professional business emails (@yourdomain)",
            "📊 Resource cluster capacity graphs"
          ];
        } else {
          title = "Wesi Core SaaS Workspace";
          desc = "Visual task dashboards, user registries, and secure member portal integrations.";
          features = [
            "🔒 Dual-factor member authentication",
            "📊 Progress metrics and CSV generation",
            "✉️ Direct system warning email dispatchers"
          ];
        }
    }

    // Add extra features based on user challenges
    if (hasAuth && !features.some(f => f.includes("Auth") || f.includes("login") || f.includes("factor"))) {
      features.push("🔒 Custom client sign-up / sign-in systems");
    }
    if (hasHosting && !features.some(f => f.includes("domain") || f.includes("hosting"))) {
      features.push("🌐 Managed domain & custom mailboxes setup");
    }

    return { title, desc, features };
  };

  const recommendation = getRecommendation();

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 font-sans text-white">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-slate-950/70 backdrop-blur-md cursor-pointer"
        />

        {/* Modal Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.93, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.93, y: 15 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-[#07050e]/95 p-6 md:p-8 shadow-[0_0_60px_rgba(139,92,246,0.3)]"
        >
          {/* Neon mesh glows */}
          <div className="absolute -top-20 -left-20 -z-10 h-44 w-44 rounded-full bg-violet-600/20 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 -z-10 h-44 w-44 rounded-full bg-sky-500/10 blur-3xl" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>

          {/* Header */}
          <div className="border-b border-white/5 pb-4 mb-6">
            <span className="text-[10px] font-mono font-bold tracking-widest text-brand-electric uppercase bg-brand-purple/10 border border-brand-purple/20 px-2.5 py-0.5 rounded-full">
              SaaS Solution Architect
            </span>
            <h2 className="font-orbitron text-xl sm:text-2xl font-black tracking-wider mt-3">
              TELL US YOUR NEEDS
            </h2>
            <p className="text-xs text-slate-400 mt-1 font-mono">
              Step {quiz.step} of 3: {quiz.step === 1 ? "Select your industry niche" : quiz.step === 2 ? "Identify operational challenges" : "Your customized cloud solution blueprint"}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: Industry Selection */}
            {quiz.step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <p className="text-sm text-slate-300 mb-4">
                  To tailor the right database structure and features, what industry is your business or project based in?
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {INDUSTRIES.map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleSelectIndustry(item.id)}
                        className="flex items-start gap-4 text-left p-4 rounded-xl border border-white/5 bg-white/5 hover:border-brand-purple/50 hover:bg-brand-purple/5 transition-all cursor-pointer group active:scale-98"
                      >
                        <div className="p-2.5 rounded-lg bg-brand-purple/10 text-brand-electric group-hover:bg-brand-purple/20 transition-all shrink-0">
                          <Icon size={18} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white group-hover:text-brand-electric transition-all text-sm">
                            {item.name}
                          </h4>
                          <p className="text-xs text-slate-400 mt-1 leading-normal">
                            {item.desc}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Step 2: Challenges Selection */}
            {quiz.step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                <p className="text-sm text-slate-300 mb-4">
                  What specific modules or requirements are you looking to establish today? (Select all that apply)
                </p>
                
                <div className="space-y-3">
                  {CHALLENGES.map(item => {
                    const isSelected = quiz.challenges.includes(item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleToggleChallenge(item.id)}
                        className={`w-full flex items-center justify-between text-left p-4 rounded-xl border transition-all cursor-pointer group ${
                          isSelected 
                            ? "bg-brand-purple/10 border-brand-purple/50 text-white" 
                            : "bg-white/5 border-white/5 hover:border-white/20 text-slate-300"
                        }`}
                      >
                        <span className="text-xs font-mono font-medium">{item.text}</span>
                        <div className="text-brand-electric shrink-0">
                          {isSelected ? (
                            <CheckSquare size={18} className="fill-brand-purple/20" />
                          ) : (
                            <Square size={18} className="text-slate-500 group-hover:text-slate-400" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-white/5 mt-6 gap-4 font-mono text-xs">
                  <button
                    onClick={() => setQuiz(prev => ({ ...prev, step: 1 }))}
                    className="text-slate-400 hover:text-white transition-colors cursor-pointer"
                  >
                    ← Back to Step 1
                  </button>
                  
                  <button
                    onClick={() => setQuiz(prev => ({ ...prev, step: 3 }))}
                    className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl bg-brand-purple hover:bg-brand-purple/85 text-white font-bold transition-all shadow-[0_0_15px_rgba(139,92,246,0.3)] active:scale-95 cursor-pointer"
                  >
                    <span>Analyze Blueprint</span>
                    <ArrowRight size={13} />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Recommendation Results */}
            {quiz.step === 3 && recommendation && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                <div className="relative overflow-hidden rounded-xl border border-brand-purple/30 bg-brand-purple/10 p-5 md:p-6 shadow-[0_0_20px_rgba(139,92,246,0.2)]">
                  {/* Glowing light mesh */}
                  <div className="absolute top-0 right-0 h-16 w-16 bg-brand-electric/20 blur-xl" />
                  
                  <div className="flex items-center gap-2 text-brand-electric text-xs font-mono font-bold uppercase mb-2">
                    <Zap size={14} className="fill-brand-electric animate-pulse" />
                    <span>Recommended Blueprint Solution:</span>
                  </div>
                  
                  <h3 className="font-orbitron font-black text-xl text-white tracking-wide">
                    {recommendation.title}
                  </h3>
                  <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                    {recommendation.desc}
                  </p>

                  <div className="mt-5 pt-4 border-t border-white/5">
                    <h5 className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-2.5">
                      Included Modules:
                    </h5>
                    <ul className="space-y-2 text-xs text-slate-300">
                      {recommendation.features.map((feat, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-purple shrink-0" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 font-mono text-xs pt-4 border-t border-white/5">
                  <button
                    onClick={handleRestart}
                    className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors cursor-pointer"
                  >
                    <RefreshCcw size={12} />
                    <span>Restart Quiz</span>
                  </button>

                  <button
                    onClick={handleLaunch}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-linear-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold transition-all shadow-[0_0_20px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] active:scale-95 cursor-pointer"
                  >
                    <span>Subscribe & Deploy</span>
                    <Sparkles size={13} className="fill-white" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
